import React, { useEffect, useMemo, useState } from "react";

const WALKER_FORM_EMBED_URL =
  "https://tally.so/embed/q4P8R9?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1";

const WalkerRecruitmentLandingPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  function openWalkerForm() {
    setIsFormOpen(true);
  }

  function closeWalkerForm() {
    setIsFormOpen(false);
  }

  useEffect(() => {
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isFormOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") closeWalkerForm();
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isFormOpen]);

  const isTablet = viewportWidth <= 960;
  const isMobile = viewportWidth <= 640;

  const styles = useMemo(() => getStyles(isTablet, isMobile), [isTablet, isMobile]);

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.shell}>
          <div style={styles.headerInner}>
            <div aria-label="WalkiesNearMe" style={styles.brand}>
              <img
                src="/walkies_logo_from_flyer_v3.png"
                alt="WalkiesNearMe"
                style={styles.brandLogo}
              />
            </div>

            <button
              type="button"
              onClick={openWalkerForm}
              style={styles.headerCta}
            >
              Join early access
            </button>
          </div>
        </div>
      </header>

      <main>
        <section style={styles.heroSection}>
          <div style={styles.shell}>
            <div style={styles.heroGrid}>
              <div style={styles.heroCopy}>
                <div style={styles.heroBadge}>
                  Early local rollout • walkers wanted now
                </div>

                <h1 style={styles.heroTitle}>
                  Dog walkers wanted in
                  <br />
                  <span style={styles.heroTitleAccent}>
                    Ipswich &amp; Colchester
                  </span>
                </h1>

                <p style={styles.heroSubtitle}>
                  Join early and be ready for local owner enquiries as
                  WalkiesNearMe launches in your area.
                </p>

                <p style={styles.heroText}>
                  We are building our first trusted local walker network before
                  opening more widely. Early joiners get first visibility as we
                  grow owner demand in the launch area.
                </p>

                <div style={styles.heroActions}>
                  <button
                    type="button"
                    onClick={openWalkerForm}
                    style={styles.primaryButton}
                  >
                    Join as a walker
                  </button>

                  <a href="#launch-area" style={styles.secondaryButton}>
                    See launch area
                  </a>
                </div>

                <div style={styles.heroNote}>
                  We are currently prioritising walkers in{" "}
                  <strong>IP and CO postcodes</strong>.
                </div>

                <div style={styles.statGrid}>
                  <div style={styles.statCard}>
                    <strong style={styles.statTitle}>Be visible early</strong>
                    <span style={styles.statText}>
                      Join before local owner demand builds so you are ready when
                      enquiries start.
                    </span>
                  </div>

                  <div style={styles.statCard}>
                    <strong style={styles.statTitle}>Stay local</strong>
                    <span style={styles.statText}>
                      We are focusing on nearby, practical coverage instead of
                      broad untargeted areas.
                    </span>
                  </div>

                  <div style={styles.statCard}>
                    <strong style={styles.statTitle}>Quick first step</strong>
                    <span style={styles.statText}>
                      Complete a short form now and we will keep your details for
                      the local rollout.
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.heroPanel}>
                <div style={styles.heroPanelCard}>
                  <div style={styles.heroPanelTop}>
                    <div style={styles.heroPanelKicker}>Launch focus</div>
                    <div style={styles.heroPanelHeadline}>
                      Building the first local walker bank
                    </div>
                  </div>

                  <img
                    src="/walkies_dog_from_flyer_v2.png"
                    alt="Happy dog out on a walk"
                    style={styles.heroDogImage}
                  />

                  <div style={styles.heroPanelList}>
                    <div style={styles.heroPanelItem}>
                      <span style={styles.tick}>✓</span>
                      <span>Ipswich and surrounding IP postcodes prioritised</span>
                    </div>
                    <div style={styles.heroPanelItem}>
                      <span style={styles.tick}>✓</span>
                      <span>Colchester and surrounding CO postcodes prioritised</span>
                    </div>
                    <div style={styles.heroPanelItem}>
                      <span style={styles.tick}>✓</span>
                      <span>Simple early-access application</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={openWalkerForm}
                    style={styles.panelButton}
                  >
                    Apply for early access
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={styles.sectionLight}>
          <div style={styles.shellNarrow}>
            <div style={styles.sectionHeading}>
              <span style={styles.kicker}>Why join now</span>
              <h2 style={styles.sectionTitle}>
                We want walker supply ready before owner demand ramps up
              </h2>
              <p style={styles.sectionText}>
                The goal right now is simple: build strong local walker coverage
                first so the platform feels credible when more owners start
                looking.
              </p>
            </div>

            <div style={styles.benefitGrid}>
              <article style={styles.benefitCard}>
                <div style={styles.benefitNumber}>1</div>
                <h3 style={styles.benefitTitle}>Get in early</h3>
                <p style={styles.benefitText}>
                  Early walkers can be prioritised as local owner interest grows
                  in the first launch areas.
                </p>
              </article>

              <article style={styles.benefitCard}>
                <div style={styles.benefitNumber}>2</div>
                <h3 style={styles.benefitTitle}>Stay relevant locally</h3>
                <p style={styles.benefitText}>
                  We are focused on practical local coverage rather than broad
                  national sign-up.
                </p>
              </article>

              <article style={styles.benefitCard}>
                <div style={styles.benefitNumber}>3</div>
                <h3 style={styles.benefitTitle}>Low-friction start</h3>
                <p style={styles.benefitText}>
                  This first step is just a short application so we can gather
                  the right local walker bank quickly.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section id="launch-area" style={styles.sectionDarkBand}>
          <div style={styles.shellNarrow}>
            <div style={styles.launchGrid}>
              <div>
                <span style={styles.kickerLight}>Launch area</span>
                <h2 style={styles.sectionTitleLight}>
                  Currently focused on Ipswich and Colchester
                </h2>
                <p style={styles.sectionTextLight}>
                  We are currently prioritising walkers in the Ipswich and
                  Colchester areas, especially those covering <strong>IP</strong>{" "}
                  and <strong>CO</strong> postcodes.
                </p>
                <p style={styles.sectionTextLight}>
                  This is a soft filter for now. If you are nearby but outside
                  those postcodes, you can still register interest and we may
                  expand later.
                </p>
              </div>

              <div style={styles.launchCard}>
                <div style={styles.launchCardTitle}>Current focus</div>
                <ul style={styles.launchList}>
                  <li style={styles.launchListItem}>IP postcodes prioritised</li>
                  <li style={styles.launchListItem}>CO postcodes prioritised</li>
                  <li style={styles.launchListItem}>Nearby walkers can still apply</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section style={styles.sectionLight}>
          <div style={styles.shellNarrow}>
            <div style={styles.sectionHeading}>
              <span style={styles.kicker}>What we need first</span>
              <h2 style={styles.sectionTitle}>Simple local walker details</h2>
              <p style={styles.sectionText}>
                We want to understand who is available, where they are based,
                and which areas they cover so we can build strong launch density
                in the right places.
              </p>
            </div>

            <div style={styles.criteriaGrid}>
              <div style={styles.criteriaCard}>
                <h3 style={styles.criteriaTitle}>Good first-fit walkers</h3>
                <ul style={styles.criteriaList}>
                  <li>Independent local dog walkers</li>
                  <li>Walkers covering Ipswich or Colchester areas</li>
                  <li>Reliable and owner-friendly</li>
                  <li>Ready to join early before full launch</li>
                </ul>
              </div>

              <div style={styles.criteriaCard}>
                <h3 style={styles.criteriaTitle}>Useful early-access details</h3>
                <ul style={styles.criteriaList}>
                  <li>Name and contact details</li>
                  <li>Postcode and areas covered</li>
                  <li>Short local service summary</li>
                  <li>Enough to contact you when rollout develops</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" style={styles.sectionCream}>
          <div style={styles.shellNarrow}>
            <div style={styles.sectionHeading}>
              <span style={styles.kicker}>How it works</span>
              <h2 style={styles.sectionTitle}>Simple early-access flow</h2>
            </div>

            <div style={styles.stepsGrid}>
              <article style={styles.stepCard}>
                <div style={styles.stepNumber}>1</div>
                <h3 style={styles.stepTitle}>Complete the short form</h3>
                <p style={styles.stepText}>
                  Tell us who you are, where you are based, and the area you
                  cover.
                </p>
              </article>

              <article style={styles.stepCard}>
                <div style={styles.stepNumber}>2</div>
                <h3 style={styles.stepTitle}>We build local coverage</h3>
                <p style={styles.stepText}>
                  We use the early data to strengthen walker density in Ipswich
                  and Colchester first.
                </p>
              </article>

              <article style={styles.stepCard}>
                <div style={styles.stepNumber}>3</div>
                <h3 style={styles.stepTitle}>We contact early joiners</h3>
                <p style={styles.stepText}>
                  As rollout develops, we can contact the strongest local early
                  walkers first.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section style={styles.ctaSection}>
          <div style={styles.shellNarrow}>
            <div style={styles.ctaBox}>
              <div style={styles.ctaCopy}>
                <span style={styles.kicker}>Join early access</span>
                <h2 style={styles.ctaTitle}>
                  Ready to join as a local walker?
                </h2>
                <p style={styles.ctaText}>
                  We are currently prioritising Ipswich and Colchester area
                  walkers. Complete the short form and we will keep your details
                  for the local rollout.
                </p>

                <div style={styles.ctaMeta}>
                  Current priority: <strong>IP and CO postcodes</strong>
                </div>
              </div>

              <div style={styles.ctaActions}>
                <button
                  type="button"
                  onClick={openWalkerForm}
                  style={styles.ctaPrimary}
                >
                  Apply for early access
                </button>
              </div>
            </div>
          </div>
        </section>

        <section style={styles.sectionLight}>
          <div style={styles.shellNarrow}>
            <div style={styles.sectionHeading}>
              <span style={styles.kicker}>Questions</span>
              <h2 style={styles.sectionTitle}>Quick FAQ</h2>
            </div>

            <div style={styles.faqList}>
              <article style={styles.faqCard}>
                <h3 style={styles.faqQuestion}>
                  Do I have to be in an IP or CO postcode?
                </h3>
                <p style={styles.faqAnswer}>
                  Those are our current priority areas, but nearby walkers can
                  still register interest.
                </p>
              </article>

              <article style={styles.faqCard}>
                <h3 style={styles.faqQuestion}>
                  Is this a full account sign-up?
                </h3>
                <p style={styles.faqAnswer}>
                  Not yet. This is an early-access step so we can build strong
                  local walker supply first.
                </p>
              </article>

              <article style={styles.faqCard}>
                <h3 style={styles.faqQuestion}>
                  What happens after I apply?
                </h3>
                <p style={styles.faqAnswer}>
                  We’ll keep your details and contact you as local rollout
                  develops in the priority launch areas.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <div style={styles.shell}>
          <div style={styles.footerInner}>
            <div>
              <strong style={styles.footerBrand}>WalkiesNearMe</strong>
              <p style={styles.footerText}>Early local walker access</p>
            </div>
          </div>
        </div>
      </footer>

      {isFormOpen ? (
        <div
          style={styles.modalOverlay}
          onClick={closeWalkerForm}
          aria-hidden="true"
        >
          <div
            style={styles.modalCard}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Join early access form"
          >
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Join early access</div>
                <div style={styles.modalSubtitle}>
                  Complete the short form to register your interest.
                </div>
              </div>

              <button
                type="button"
                onClick={closeWalkerForm}
                style={styles.modalCloseButton}
                aria-label="Close form"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <iframe
                src={WALKER_FORM_EMBED_URL}
                title="WalkiesNearMe walker early access form"
                style={styles.modalIframe}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function getStyles(
  isTablet: boolean,
  isMobile: boolean
): Record<string, React.CSSProperties> {
  return {
    page: {
      minHeight: "100vh",
      background:
        "radial-gradient(circle at top right, rgba(242,138,22,0.14), transparent 28%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
      color: "#18201d",
    },

    shell: {
      maxWidth: 1180,
      margin: "0 auto",
      padding: isMobile ? "0 14px" : "0 16px",
    },

    shellNarrow: {
      maxWidth: 980,
      margin: "0 auto",
      padding: isMobile ? "0 14px" : "0 16px",
    },

    header: {
      position: "sticky",
      top: 0,
      zIndex: 20,
      background: "rgba(33,58,51,0.92)",
      backdropFilter: "blur(10px)",
      borderBottom: "1px solid rgba(255,255,255,0.08)",
    },

    headerInner: {
      minHeight: isMobile ? 66 : 74,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      padding: isMobile ? "10px 0" : "12px 0",
    },

    brand: {
      display: "inline-flex",
      alignItems: "center",
      minWidth: 0,
    },

    brandLogo: {
      height: isMobile ? 38 : 44,
      width: "auto",
      display: "block",
    },

    headerCta: {
      border: 0,
      borderRadius: 14,
      padding: isMobile ? "11px 14px" : "12px 16px",
      background: "#f28a16",
      color: "#ffffff",
      fontWeight: 800,
      fontSize: isMobile ? 13 : 14,
      cursor: "pointer",
      whiteSpace: "nowrap",
      flexShrink: 0,
    },

    heroSection: {
      padding: isMobile ? "18px 0 14px" : "28px 0 24px",
    },

    heroGrid: {
      display: "grid",
      gridTemplateColumns: isTablet
        ? "1fr"
        : "minmax(0, 1.15fr) minmax(300px, 420px)",
      gap: isMobile ? 14 : 24,
      alignItems: "start",
    },

    heroCopy: {
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: isMobile ? 22 : 30,
      padding: isMobile ? 18 : 28,
      color: "#ffffff",
      boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
    },

    heroBadge: {
      display: "inline-flex",
      alignItems: "center",
      borderRadius: 999,
      padding: isMobile ? "7px 10px" : "8px 12px",
      background: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      fontWeight: 800,
      fontSize: isMobile ? 12 : 13,
      marginBottom: isMobile ? 14 : 18,
    },

    heroTitle: {
      margin: "0 0 14px",
      fontSize: isMobile ? 36 : isTablet ? 44 : 54,
      lineHeight: isMobile ? 1 : 0.95,
      letterSpacing: "-0.04em",
      fontWeight: 900,
    },

    heroTitleAccent: {
      color: "#ffd7a5",
    },

    heroSubtitle: {
      margin: "0 0 14px",
      fontSize: isMobile ? 20 : isTablet ? 22 : 24,
      lineHeight: 1.2,
      fontWeight: 800,
      color: "#fff6ea",
    },

    heroText: {
      margin: "0 0 20px",
      fontSize: isMobile ? 16 : 17,
      lineHeight: 1.7,
      color: "rgba(255,255,255,0.9)",
      maxWidth: 720,
    },

    heroActions: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: 12,
      flexWrap: "wrap",
      marginBottom: 14,
    },

    primaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 52,
      padding: "14px 18px",
      borderRadius: 16,
      border: "none",
      background: "#f28a16",
      color: "#ffffff",
      fontWeight: 800,
      fontSize: 16,
      cursor: "pointer",
      width: isMobile ? "100%" : undefined,
    },

    secondaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 52,
      padding: "14px 18px",
      borderRadius: 16,
      border: "1px solid rgba(255,255,255,0.18)",
      background: "rgba(255,255,255,0.08)",
      color: "#ffffff",
      fontWeight: 800,
      fontSize: 16,
      textDecoration: "none",
      width: isMobile ? "100%" : undefined,
      boxSizing: "border-box",
    },

    heroNote: {
      marginBottom: isMobile ? 16 : 22,
      fontSize: 15,
      lineHeight: 1.6,
      color: "#fff2df",
    },

    statGrid: {
      display: "grid",
      gridTemplateColumns: isMobile
        ? "1fr"
        : "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 12,
    },

    statCard: {
      borderRadius: 18,
      padding: isMobile ? 14 : 16,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.1)",
    },

    statTitle: {
      display: "block",
      marginBottom: 8,
      fontSize: 16,
      color: "#ffffff",
    },

    statText: {
      display: "block",
      fontSize: 14,
      lineHeight: 1.6,
      color: "rgba(255,255,255,0.84)",
    },

    heroPanel: {
      display: "flex",
      alignItems: "flex-start",
    },

    heroPanelCard: {
      width: "100%",
      background: "#f5f0e8",
      borderRadius: isMobile ? 22 : 30,
      padding: isMobile ? 16 : 22,
      boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      alignSelf: "start",
    },

    heroPanelTop: {
      marginBottom: isMobile ? 12 : 16,
    },

    heroPanelKicker: {
      fontSize: 13,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: "rgba(24,32,29,0.58)",
      marginBottom: 8,
    },

    heroPanelHeadline: {
      fontSize: isMobile ? 22 : 28,
      lineHeight: 1.08,
      fontWeight: 900,
      color: "#213a33",
    },

    heroDogImage: {
      width: "100%",
      height: isMobile ? 180 : isTablet ? 220 : undefined,
      maxHeight: isMobile ? 180 : isTablet ? 220 : 320,
      borderRadius: 22,
      objectFit: "cover",
      marginBottom: isMobile ? 14 : 18,
      background: "#fff",
      display: "block",
    },

    heroPanelList: {
      display: "grid",
      gap: isMobile ? 8 : 10,
      marginBottom: isMobile ? 14 : 18,
    },

    heroPanelItem: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      fontSize: isMobile ? 14 : 15,
      lineHeight: 1.55,
      color: "#213a33",
      fontWeight: 700,
    },

    tick: {
      color: "#f28a16",
      fontWeight: 900,
    },

    panelButton: {
      minHeight: 52,
      border: 0,
      borderRadius: 16,
      padding: "14px 18px",
      background: "#213a33",
      color: "#ffffff",
      fontWeight: 800,
      fontSize: 16,
      cursor: "pointer",
      width: "100%",
    },

    sectionLight: {
      background: "#f5f0e8",
      padding: isMobile ? "36px 0" : "54px 0",
    },

    sectionDarkBand: {
      background: "#213a33",
      padding: isMobile ? "36px 0" : "54px 0",
      color: "#ffffff",
    },

    sectionCream: {
      background: "#fbf7f1",
      padding: isMobile ? "36px 0" : "54px 0",
    },

    sectionHeading: {
      marginBottom: isMobile ? 20 : 26,
    },

    kicker: {
      display: "inline-block",
      marginBottom: 10,
      fontSize: 13,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: "#b45309",
    },

    kickerLight: {
      display: "inline-block",
      marginBottom: 10,
      fontSize: 13,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: "#ffd7a5",
    },

    sectionTitle: {
      margin: "0 0 12px",
      fontSize: isMobile ? 30 : 40,
      lineHeight: 1.05,
      letterSpacing: "-0.03em",
      fontWeight: 900,
      color: "#213a33",
    },

    sectionTitleLight: {
      margin: "0 0 12px",
      fontSize: isMobile ? 30 : 40,
      lineHeight: 1.05,
      letterSpacing: "-0.03em",
      fontWeight: 900,
      color: "#ffffff",
    },

    sectionText: {
      margin: 0,
      fontSize: isMobile ? 16 : 17,
      lineHeight: 1.75,
      color: "rgba(24,32,29,0.78)",
      maxWidth: 760,
    },

    sectionTextLight: {
      margin: "0 0 14px",
      fontSize: isMobile ? 16 : 17,
      lineHeight: 1.75,
      color: "rgba(255,255,255,0.9)",
      maxWidth: 760,
    },

    benefitGrid: {
      display: "grid",
      gridTemplateColumns: isMobile
        ? "1fr"
        : "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 16,
    },

    benefitCard: {
      borderRadius: 24,
      padding: isMobile ? 18 : 22,
      background: "#ffffff",
      border: "1px solid rgba(33,58,51,0.08)",
      boxShadow: "0 10px 24px rgba(0,0,0,0.05)",
    },

    benefitNumber: {
      width: 40,
      height: 40,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(242,138,22,0.14)",
      color: "#b45309",
      fontWeight: 900,
      marginBottom: 14,
    },

    benefitTitle: {
      margin: "0 0 10px",
      fontSize: isMobile ? 20 : 22,
      color: "#213a33",
    },

    benefitText: {
      margin: 0,
      fontSize: 15,
      lineHeight: 1.7,
      color: "rgba(24,32,29,0.76)",
    },

    launchGrid: {
      display: "grid",
      gridTemplateColumns: isTablet
        ? "1fr"
        : "minmax(0, 1fr) minmax(260px, 340px)",
      gap: isMobile ? 16 : 20,
      alignItems: "start",
    },

    launchCard: {
      borderRadius: 24,
      padding: isMobile ? 18 : 20,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.12)",
    },

    launchCardTitle: {
      fontSize: isMobile ? 20 : 22,
      fontWeight: 900,
      marginBottom: 12,
      color: "#ffffff",
    },

    launchList: {
      listStyle: "none",
      margin: 0,
      padding: 0,
      display: "grid",
      gap: 10,
    },

    launchListItem: {
      fontSize: 15,
      lineHeight: 1.7,
      color: "rgba(255,255,255,0.92)",
    },

    criteriaGrid: {
      display: "grid",
      gridTemplateColumns: isMobile
        ? "1fr"
        : "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 16,
    },

    criteriaCard: {
      borderRadius: 24,
      padding: isMobile ? 18 : 22,
      background: "#ffffff",
      border: "1px solid rgba(33,58,51,0.08)",
    },

    criteriaTitle: {
      margin: "0 0 12px",
      fontSize: isMobile ? 20 : 22,
      color: "#213a33",
    },

    criteriaList: {
      margin: 0,
      paddingLeft: 20,
      color: "rgba(24,32,29,0.8)",
      lineHeight: 1.8,
      fontSize: 15,
    },

    stepsGrid: {
      display: "grid",
      gridTemplateColumns: isMobile
        ? "1fr"
        : "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 16,
    },

    stepCard: {
      borderRadius: 24,
      padding: isMobile ? 18 : 22,
      background: "#ffffff",
      border: "1px solid rgba(33,58,51,0.08)",
    },

    stepNumber: {
      width: 42,
      height: 42,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#213a33",
      color: "#ffffff",
      fontWeight: 900,
      marginBottom: 14,
    },

    stepTitle: {
      margin: "0 0 10px",
      fontSize: isMobile ? 20 : 22,
      color: "#213a33",
    },

    stepText: {
      margin: 0,
      fontSize: 15,
      lineHeight: 1.7,
      color: "rgba(24,32,29,0.78)",
    },

    ctaSection: {
      background:
        "radial-gradient(circle at top right, rgba(242,138,22,0.18), transparent 25%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
      padding: isMobile ? "36px 0" : "54px 0",
    },

    ctaBox: {
      borderRadius: isMobile ? 22 : 30,
      padding: isMobile ? 18 : 28,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
      display: "grid",
      gridTemplateColumns: isTablet
        ? "1fr"
        : "minmax(0, 1fr) minmax(240px, 300px)",
      gap: isMobile ? 16 : 20,
      alignItems: "center",
    },

    ctaCopy: {
      color: "#ffffff",
    },

    ctaTitle: {
      margin: "0 0 12px",
      fontSize: isMobile ? 30 : 40,
      lineHeight: 1.05,
      letterSpacing: "-0.03em",
      fontWeight: 900,
    },

    ctaText: {
      margin: "0 0 12px",
      fontSize: isMobile ? 16 : 17,
      lineHeight: 1.75,
      color: "rgba(255,255,255,0.9)",
    },

    ctaMeta: {
      fontSize: 15,
      color: "#fff2df",
      lineHeight: 1.6,
    },

    ctaActions: {
      display: "grid",
      gap: 12,
    },

    ctaPrimary: {
      minHeight: 54,
      border: 0,
      borderRadius: 16,
      padding: "14px 18px",
      background: "#f28a16",
      color: "#ffffff",
      fontWeight: 800,
      fontSize: 16,
      cursor: "pointer",
      width: "100%",
    },

    faqList: {
      display: "grid",
      gap: 14,
    },

    faqCard: {
      borderRadius: 22,
      padding: isMobile ? 18 : 20,
      background: "#ffffff",
      border: "1px solid rgba(33,58,51,0.08)",
    },

    faqQuestion: {
      margin: "0 0 8px",
      fontSize: isMobile ? 20 : 22,
      color: "#213a33",
    },

    faqAnswer: {
      margin: 0,
      fontSize: 15,
      lineHeight: 1.75,
      color: "rgba(24,32,29,0.78)",
    },

    footer: {
      background: "#182925",
      color: "#ffffff",
      padding: isMobile ? "18px 0" : "22px 0",
    },

    footerInner: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
    },

    footerBrand: {
      display: "block",
      marginBottom: 6,
    },

    footerText: {
      margin: 0,
      color: "rgba(255,255,255,0.72)",
      fontSize: 14,
    },

    modalOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(10, 16, 14, 0.72)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: isMobile ? 10 : 16,
      zIndex: 1000,
    },

    modalCard: {
      width: "100%",
      maxWidth: 860,
      maxHeight: "92vh",
      background: "#f5f0e8",
      borderRadius: isMobile ? 18 : 24,
      boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },

    modalHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      padding: isMobile ? "14px 16px" : "18px 20px",
      borderBottom: "1px solid rgba(24,32,29,0.08)",
      background: "#fbf7f1",
    },

    modalTitle: {
      fontSize: isMobile ? 22 : 28,
      lineHeight: 1.05,
      fontWeight: 900,
      color: "#213a33",
      marginBottom: 6,
    },

    modalSubtitle: {
      fontSize: 15,
      lineHeight: 1.6,
      color: "rgba(24,32,29,0.72)",
    },

    modalCloseButton: {
      width: 44,
      height: 44,
      borderRadius: 999,
      border: "1px solid rgba(24,32,29,0.14)",
      background: "#ffffff",
      color: "#213a33",
      fontSize: 28,
      lineHeight: 1,
      cursor: "pointer",
      flexShrink: 0,
    },

    modalBody: {
      padding: 0,
      overflow: "auto",
      background: "#ffffff",
      minHeight: isMobile ? 320 : 420,
    },

    modalIframe: {
      width: "100%",
      height: isMobile ? "72vh" : "78vh",
      minHeight: isMobile ? 420 : 620,
      border: "none",
      display: "block",
      background: "#ffffff",
    },
  };
}

export default WalkerRecruitmentLandingPage;